import { useState, useEffect } from 'react'
import { Modal, Form, Button, Table, Badge, Alert, Row, Col, Spinner, Card } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { getDisplaySerieFolio } from '../../utils/invoiceDisplay'
import { formatDateOnlyEsMX, getTodayDateOnlyMx } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'
import RevisarAsientoContableModal from './RevisarAsientoContableModal'
import {
  FORMA_PAGO_SAT_OPTIONS_PAGO,
  formaPagoRequiereCuentaBancaria,
  formaPagoToPaymentMethod,
} from '../../constants/formaPagoSat'

const ApplyPaymentModal = ({ show, onHide, invoice, bill, onSuccess }) => {
  const queryClient = useQueryClient()
  const [selectedPayment, setSelectedPayment] = useState('')
  const [amountToApply, setAmountToApply] = useState(0)
  const [createNewPayment, setCreateNewPayment] = useState(false)
  const [newPaymentData, setNewPaymentData] = useState({
    payment_date: getTodayDateOnlyMx(),
    amount: 0,
    forma_pago: '01',
    payment_method: 'CASH',
    reference: '',
    bank_account: '',
    moneda: 'MXN',
    tipo_cambio: '',
  })

  const isBill = !!bill
  const item = bill || invoice
  const clienteId = typeof item?.client === 'object' ? item.client?.id : (item?.client || item?.client_id)
  const tipoPago = isBill ? 'egreso' : 'ingreso'
  const facturaMoneda = ((item?.moneda || 'MXN') + '').toUpperCase()

  const { data: catalogoMonedas } = useQuery(
    'catalogo-monedas-sat-payment',
    async () => {
      const response = await api.get('/financial/invoices/catalogo-moneda/')
      return response.data.results || response.data || []
    },
    { enabled: !!show, staleTime: 600000 }
  )

  // Obtener cuentas bancarias del cliente
  const { data: bankAccounts } = useQuery(
    ['bank-accounts', clienteId],
    async () => {
      if (!clienteId) return []
      const response = await api.get('/financial/bank-accounts/', {
        params: { cliente_id: clienteId, is_active: true }
      })
      return response.data.results || response.data || []
    },
    { enabled: !!show && !!clienteId }
  )

  // Obtener pagos disponibles del cliente (ingreso para facturas, egreso para bills)
  const { data: availablePayments, isLoading: loadingPayments } = useQuery(
    ['available-payments', clienteId, tipoPago],
    async () => {
      if (!clienteId) return []
      const response = await api.get('/financial/payments/', {
        params: {
          cliente_id: clienteId,
          estado: 'pendiente',
          tipo_pago: tipoPago
        }
      })
      const payments = response.data.results || response.data || []
      return payments.filter(p => parseFloat(p.monto_disponible || p.amount || 0) > 0.01)
    },
    { enabled: !!show && !!clienteId }
  )

  // Pagos ya aplicados a esta factura (detalle fresco al abrir el modal)
  const docId = isBill ? bill?.id : invoice?.id
  const { data: docDetail, isLoading: loadingDocDetail } = useQuery(
    ['apply-payment-doc-detail', isBill ? 'bill' : 'invoice', docId],
    async () => {
      if (!docId) return null
      const url = isBill ? `/financial/bills/${docId}/` : `/financial/invoices/${docId}/`
      const response = await api.get(url)
      return response.data
    },
    { enabled: !!show && !!docId, staleTime: 0 }
  )

  const aplicacionesAsociadas = (
    docDetail?.aplicaciones_pago
    || item?.aplicaciones_pago
    || []
  ).filter((a) => (a.estado || 'aplicado') === 'aplicado')

  const saldoPendienteDoc = (() => {
    const src = docDetail || item
    if (!src) return 0
    if (src.monto_pendiente !== undefined && src.monto_pendiente !== null && src.monto_pendiente !== '') {
      return parseFloat(src.monto_pendiente)
    }
    return parseFloat(src.total || 0)
  })()

  // Si no hay pagos pendientes disponibles, abrir directo en "Crear Nuevo Pago"
  useEffect(() => {
    if (!show || loadingPayments) return
    if ((!availablePayments || availablePayments.length === 0) && saldoPendienteDoc > 0.01) {
      setCreateNewPayment(true)
    }
  }, [show, loadingPayments, availablePayments, saldoPendienteDoc])

  const [pendingApply, setPendingApply] = useState(null) // { paymentId, amount }
  const [showAsientoModal, setShowAsientoModal] = useState(false)
  const [asientoPropuesta, setAsientoPropuesta] = useState(null)
  const [asientoPreviewError, setAsientoPreviewError] = useState(null)
  const [loadingAsientoPreview, setLoadingAsientoPreview] = useState(false)

  const aplicacionPayload = (amount, lineas_contables = null) => {
    const base = isBill
      ? { bill_id: bill.id, monto: parseFloat(amount) }
      : { factura_id: invoice.id, monto: parseFloat(amount) }
    if (lineas_contables?.length) base.lineas_contables = lineas_contables
    return base
  }

  const openPreviewAplicacion = async (paymentId, amount) => {
    setPendingApply({ paymentId, amount })
    setShowAsientoModal(true)
    setLoadingAsientoPreview(true)
    setAsientoPreviewError(null)
    setAsientoPropuesta(null)
    try {
      const res = await api.post(`/financial/payments/${paymentId}/preview-asiento-aplicacion/`, {
        aplicaciones: [aplicacionPayload(amount)],
      })
      const prop = (res.data.propuestas || [])[0] || null
      if (prop?.omitir) {
        // Nómina: aplicar directo sin modal
        setShowAsientoModal(false)
        applyPaymentMutation.mutate({ paymentId, amount, lineas_contables: [] })
        return
      }
      setAsientoPropuesta(prop)
    } catch (error) {
      setAsientoPreviewError(error.response?.data?.error || 'No se pudo cargar el asiento del cobro')
    } finally {
      setLoadingAsientoPreview(false)
    }
  }

  // Mutación para aplicar pago
  const applyPaymentMutation = useMutation(
    async ({ paymentId, amount, lineas_contables }) => {
      return api.post(`/financial/payments/${paymentId}/aplicar/`, {
        aplicaciones: [aplicacionPayload(amount, lineas_contables)],
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices')
        queryClient.invalidateQueries('bills')
        queryClient.invalidateQueries('payments')
        queryClient.invalidateQueries('available-payments')
        queryClient.invalidateQueries('apply-payment-doc-detail')
        toast.success('Pago aplicado exitosamente')
        setShowAsientoModal(false)
        setPendingApply(null)
        onSuccess && onSuccess()
        handleClose()
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Error al aplicar pago'
        toast.error(message)
      }
    }
  )

  // Mutación para crear pago y luego revisar asiento
  const createAndPreviewMutation = useMutation(
    async (paymentData) => {
      if (formaPagoRequiereCuentaBancaria(paymentData.forma_pago) && !paymentData.bank_account) {
        throw new Error('La cuenta bancaria es requerida para transferencias y cheques')
      }

      const amountRaw = parseFloat(paymentData.amount) || 0
      const amountSafe = Number(amountRaw.toFixed(2))
      const payload = {
        cliente: clienteId,
        payment_date: paymentData.payment_date,
        amount: amountSafe,
        forma_pago: paymentData.forma_pago,
        payment_method: formaPagoToPaymentMethod(paymentData.forma_pago),
        reference: paymentData.reference || '',
        tipo_pago: tipoPago,
        estado: 'pendiente',
        moneda: (paymentData.moneda || 'MXN').toUpperCase(),
      }
      if ((payload.moneda || 'MXN') !== 'MXN') {
        payload.tipo_cambio = parseFloat(paymentData.tipo_cambio) || 0
      }
      if (paymentData.bank_account) {
        payload.bank_account = parseInt(paymentData.bank_account, 10)
      }

      const createResponse = await api.post('/financial/payments/', payload)
      return createResponse.data
    },
    {
      onSuccess: async (payment) => {
        queryClient.invalidateQueries('payments')
        queryClient.invalidateQueries('available-payments')
        if (payment.id && parseFloat(amountToApply) > 0) {
          await openPreviewAplicacion(payment.id, amountToApply)
        }
      },
      onError: (error) => {
        const message = error.response?.data?.error || error.message || 'Error al crear pago'
        toast.error(message)
      }
    }
  )

  useEffect(() => {
    if (item || docDetail) {
      const src = docDetail || item
      const pendienteVal = (src.monto_pendiente !== undefined && src.monto_pendiente !== null && src.monto_pendiente !== '')
        ? parseFloat(src.monto_pendiente)
        : parseFloat(src.total || 0)
      setAmountToApply(pendienteVal > 0 ? pendienteVal : 0)
      setNewPaymentData(prev => ({
        ...prev,
        amount: pendienteVal > 0 ? pendienteVal : 0,
      }))
    }
  }, [item, docDetail])

  const handleClose = () => {
    setSelectedPayment('')
    setAmountToApply(0)
    setCreateNewPayment(false)
    setNewPaymentData({
      payment_date: getTodayDateOnlyMx(),
      amount: 0,
      forma_pago: '01',
      payment_method: 'CASH',
      reference: '',
      bank_account: '',
      moneda: 'MXN',
      tipo_cambio: '',
    })
    onHide()
  }

  const handleApplyPayment = () => {
    if (!selectedPayment) {
      toast.error('Selecciona un pago')
      return
    }

    const payment = availablePayments.find(p => p.id === parseInt(selectedPayment))
    if (!payment) {
      toast.error('Pago no encontrado')
      return
    }

    const montoDisponible = parseFloat(payment.monto_disponible || payment.amount || 0)
    const montoAplicar = parseFloat(amountToApply)

    if (montoAplicar <= 0) {
      toast.error('El monto a aplicar debe ser mayor a 0')
      return
    }

    if (montoAplicar > montoDisponible) {
      toast.error(`El monto a aplicar ($${montoAplicar.toFixed(2)}) excede el disponible ($${montoDisponible.toFixed(2)})`)
      return
    }

    const pendienteCheck = saldoPendienteDoc
    if (montoAplicar > pendienteCheck) {
      toast.error(`El monto a aplicar ($${montoAplicar.toFixed(2)}) excede el saldo pendiente ($${pendienteCheck.toFixed(2)})`)
      return
    }

    openPreviewAplicacion(parseInt(selectedPayment, 10), montoAplicar)
  }

  const handleCreateAndApply = () => {
    const montoAplicar = parseFloat(amountToApply)
    const montoPago = parseFloat(newPaymentData.amount)

    if (montoAplicar <= 0) {
      toast.error('El monto a aplicar debe ser mayor a 0')
      return
    }

    if (montoAplicar > montoPago) {
      toast.error('El monto a aplicar no puede ser mayor al monto del pago')
      return
    }

    const pendienteCheck = saldoPendienteDoc
    if (montoAplicar > pendienteCheck) {
      toast.error(`El monto a aplicar ($${montoAplicar.toFixed(2)}) excede el saldo pendiente ($${pendienteCheck.toFixed(2)})`)
      return
    }

    // Validar que TRANSFER y CHECK requieran bank_account
    if (formaPagoRequiereCuentaBancaria(newPaymentData.forma_pago) && !newPaymentData.bank_account) {
      toast.error('La cuenta bancaria es requerida para transferencias y cheques')
      return
    }

    if ((newPaymentData.moneda || 'MXN').toUpperCase() !== 'MXN') {
      const tc = parseFloat(newPaymentData.tipo_cambio)
      if (!tc || tc <= 0) {
        toast.error('Tipo de cambio obligatorio y mayor a 0 cuando la moneda no es MXN')
        return
      }
    }

    createAndPreviewMutation.mutate(newPaymentData)
  }

  if (!item) return null

  const pendiente = saldoPendienteDoc
  const isLoading = applyPaymentMutation.isLoading || createAndPreviewMutation.isLoading || loadingAsientoPreview
  const esPue = !isBill && ((invoice?.metodo_pago || docDetail?.metodo_pago || '') + '').toString().trim().toUpperCase() === 'PUE'
  // Solo confirmar "no timbrada" cuando ya cargó el detalle (el listado a veces no trae cfdi_uuid).
  const uuidFiscal = ((invoice?.cfdi_uuid || docDetail?.cfdi_uuid || '') + '').toString().trim()
  const noTimbrada = !isBill && !loadingDocDetail && !uuidFiscal
  // PUE con saldo pendiente SÍ puede cobrar manualmente; no se usa esPue para deshabilitar.
  const puedeAplicar = pendiente > 0.01 && !noTimbrada

  return (
    <>
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isBill ? `Aplicar Pago a Factura Recibida ${bill?.uuid?.slice(0, 8) || ''}` : `Aplicar Pago a Factura ${getDisplaySerieFolio(invoice?.invoice_number || docDetail?.invoice_number)}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Información de la factura/bill */}
        <Alert variant="info" className="mb-3">
          <Row>
            <Col md={4}>
              <strong>Total:</strong> {formatCurrency((docDetail || item).total || 0)}
            </Col>
            <Col md={4}>
              <strong>Pagado:</strong> {formatCurrency((docDetail || item).monto_pagado || 0)}
            </Col>
            <Col md={4}>
              <strong>Saldo Pendiente:</strong> {formatCurrency(pendiente)}
            </Col>
          </Row>
        </Alert>

        {aplicacionesAsociadas.length > 0 && (
          <Card className="mb-3 border-secondary">
            <Card.Header className="py-2 bg-light">
              <strong>Pagos ya asociados</strong>
              <span className="text-muted small ms-2">({aplicacionesAsociadas.length})</span>
            </Card.Header>
            <Card.Body className="py-2">
              <Table responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Pago</th>
                    <th>Fecha</th>
                    <th>Referencia</th>
                    <th>Monto aplicado</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {aplicacionesAsociadas.map((apl) => (
                    <tr key={apl.id}>
                      <td>#{apl.payment_id || apl.pago || '—'}</td>
                      <td>{formatDateOnlyEsMX(apl.payment_date || apl.fecha_aplicacion, {}, '—')}</td>
                      <td>{apl.payment_reference || '—'}</td>
                      <td>{formatCurrency(apl.monto || 0)}</td>
                      <td>
                        <Badge bg={apl.estado === 'aplicado' ? 'success' : 'secondary'}>
                          {apl.estado || 'aplicado'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {esPue && (
          <Alert variant="info">
            Esta factura es <strong>PUE</strong>. El cobro suele registrarse al timbrar;
            si aún tiene saldo (importada o sin auto-pago), <strong>sí puede aplicar un pago manual</strong>.
            No requiere complemento de pago (REP).
          </Alert>
        )}
        {loadingDocDetail && (
          <div className="text-center py-2 mb-2">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="text-muted small">Cargando datos de la factura…</span>
          </div>
        )}
        {noTimbrada && (
          <Alert variant="warning">
            La factura debe estar <strong>timbrada</strong> antes de aplicar un pago.
          </Alert>
        )}
        {!puedeAplicar && !noTimbrada && !loadingDocDetail && pendiente <= 0.01 && (
          <Alert variant="success">
            Esta factura no tiene saldo pendiente. Los pagos asociados se muestran arriba.
          </Alert>
        )}

        {/* Tabs: Usar pago existente o crear nuevo — NUNCA se deshabilitan por ser PUE */}
        <div className="mb-3">
          <div className="btn-group w-100" role="group">
            <Button
              variant={!createNewPayment ? 'primary' : 'outline-primary'}
              onClick={() => setCreateNewPayment(false)}
              disabled={!puedeAplicar}
            >
              Usar Pago Existente
            </Button>
            <Button
              variant={createNewPayment ? 'primary' : 'outline-primary'}
              onClick={() => setCreateNewPayment(true)}
              disabled={!puedeAplicar}
            >
              Crear Nuevo Pago
            </Button>
          </div>
        </div>

        {puedeAplicar && !createNewPayment ? (
          /* Usar pago existente */
          <div>
            {loadingPayments ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : availablePayments && availablePayments.length > 0 ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Seleccionar Pago</Form.Label>
                  <Form.Select
                    value={selectedPayment}
                    onChange={(e) => {
                      setSelectedPayment(e.target.value)
                      const payment = availablePayments.find(p => p.id === parseInt(e.target.value))
                      if (payment) {
                        const disponible = parseFloat(payment.monto_disponible || 0)
                        const maxAplicar = Math.min(disponible, pendiente)
                        setAmountToApply(maxAplicar)
                      }
                    }}
                  >
                    <option value="">Seleccionar pago...</option>
                    {availablePayments.map(payment => (
                      <option key={payment.id} value={payment.id}>
                        Pago #{payment.id} - {payment.moneda || 'MXN'} {formatCurrency(payment.amount || 0)} - Disponible: {formatCurrency(payment.monto_disponible || 0)} - {formatDateOnlyEsMX(payment.payment_date, {}, '—')}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {selectedPayment && (() => {
                  const sel = availablePayments.find(p => p.id === parseInt(selectedPayment, 10))
                  const monPago = (sel?.moneda || 'MXN').toUpperCase()
                  if (monPago !== facturaMoneda) {
                    return (
                      <Alert variant="secondary" className="py-2">
                        <small>
                          Moneda del cobro ({monPago}) distinta a la factura ({facturaMoneda}).
                          Al generar el REP se calculará EquivalenciaDR (editable).
                        </small>
                      </Alert>
                    )
                  }
                  return null
                })()}

                {selectedPayment && (
                  <Form.Group className="mb-3">
                    <Form.Label>Monto a Aplicar</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      max={Math.min(
                        parseFloat(availablePayments.find(p => p.id === parseInt(selectedPayment))?.monto_disponible || 0),
                        pendiente
                      )}
                      value={amountToApply}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0
                        const payment = availablePayments.find(p => p.id === parseInt(selectedPayment))
                        const disponible = parseFloat(payment?.monto_disponible || 0)
                        const maxValue = Math.min(disponible, pendiente)
                        setAmountToApply(Math.min(value, maxValue))
                      }}
                    />
                    <Form.Text className="text-muted">
                      Máximo: {formatCurrency(Math.min(
                        parseFloat(availablePayments.find(p => p.id === parseInt(selectedPayment))?.monto_disponible || 0),
                        pendiente
                      ))}
                    </Form.Text>
                  </Form.Group>
                )}
              </>
            ) : (
              <Alert variant="warning" className="d-flex justify-content-between align-items-center">
                <span>No hay pagos pendientes disponibles para este cliente.</span>
                <Button variant="outline-primary" size="sm" onClick={() => setCreateNewPayment(true)}>
                  Crear Nuevo Pago
                </Button>
              </Alert>
            )}
          </div>
        ) : puedeAplicar ? (
          /* Crear nuevo pago */
          <div>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de Pago *</Form.Label>
                  <Form.Control
                    type="date"
                    value={newPaymentData.payment_date}
                    onChange={(e) => setNewPaymentData({ ...newPaymentData, payment_date: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Forma de Pago *</Form.Label>
                  <Form.Select
                    value={newPaymentData.forma_pago}
                    onChange={(e) => {
                      const forma = e.target.value
                      setNewPaymentData({
                        ...newPaymentData,
                        forma_pago: forma,
                        payment_method: formaPagoToPaymentMethod(forma),
                        bank_account: formaPagoRequiereCuentaBancaria(forma) ? newPaymentData.bank_account : '',
                      })
                    }}
                    required
                  >
                    {FORMA_PAGO_SAT_OPTIONS_PAGO.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {formaPagoRequiereCuentaBancaria(newPaymentData.forma_pago) && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cuenta Bancaria *</Form.Label>
                    <Form.Select
                      value={newPaymentData.bank_account || ''}
                      onChange={(e) => setNewPaymentData({ ...newPaymentData, bank_account: e.target.value })}
                      required
                      isInvalid={!newPaymentData.bank_account && formaPagoRequiereCuentaBancaria(newPaymentData.forma_pago)}
                    >
                      <option value="">Seleccionar cuenta bancaria...</option>
                      {bankAccounts?.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.name} - {account.account_number} ({account.bank_name || 'N/A'})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Requerido para transferencias y cheques
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Monto del Pago *</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={newPaymentData.amount}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0
                      setNewPaymentData({ ...newPaymentData, amount: value })
                      // Auto-ajustar monto a aplicar si es mayor al nuevo monto
                      if (parseFloat(amountToApply) > value) {
                        setAmountToApply(value)
                      }
                    }}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Referencia</Form.Label>
                  <Form.Control
                    type="text"
                    value={newPaymentData.reference}
                    onChange={(e) => setNewPaymentData({ ...newPaymentData, reference: e.target.value })}
                    placeholder="Número de referencia, cheque, etc."
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Moneda del cobro (MonedaP) *</Form.Label>
                  <Form.Select
                    value={newPaymentData.moneda || 'MXN'}
                    onChange={(e) => {
                      const mon = (e.target.value || 'MXN').toUpperCase()
                      setNewPaymentData({
                        ...newPaymentData,
                        moneda: mon,
                        tipo_cambio: mon === 'MXN' ? '' : newPaymentData.tipo_cambio,
                      })
                    }}
                  >
                    {(catalogoMonedas?.length
                      ? catalogoMonedas
                      : [{ codigo: 'MXN', descripcion: 'Peso Mexicano' }, { codigo: 'USD', descripcion: 'Dólar' }]
                    ).map((m) => (
                      <option key={m.codigo || m.id} value={m.codigo}>
                        {m.codigo}{m.descripcion ? ` — ${m.descripcion}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de cambio (TipoCambioP)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.000001"
                    value={(newPaymentData.moneda || 'MXN') === 'MXN' ? '1' : (newPaymentData.tipo_cambio || '')}
                    disabled={(newPaymentData.moneda || 'MXN') === 'MXN'}
                    onChange={(e) => setNewPaymentData({ ...newPaymentData, tipo_cambio: e.target.value })}
                    placeholder="MXN por 1 unidad"
                  />
                  <Form.Text className="text-muted">
                    Si MonedaP = MXN se emite TipoCambioP = 1. Solo editable si MonedaP ≠ MXN.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Monto a Aplicar a esta Factura</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                max={Math.min(parseFloat(newPaymentData.amount || 0), pendiente)}
                value={amountToApply}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0
                  const maxValue = Math.min(parseFloat(newPaymentData.amount || 0), pendiente)
                  setAmountToApply(Math.min(value, maxValue))
                }}
              />
              <Form.Text className="text-muted">
                Máximo: {formatCurrency(Math.min(parseFloat(newPaymentData.amount || 0), pendiente))}
              </Form.Text>
            </Form.Group>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        {puedeAplicar && !createNewPayment ? (
          <Button
            variant="primary"
            onClick={handleApplyPayment}
            disabled={isLoading || !selectedPayment || amountToApply <= 0}
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Aplicando...
              </>
            ) : (
              'Aplicar Pago'
            )}
          </Button>
        ) : puedeAplicar ? (
          <Button
            variant="primary"
            onClick={handleCreateAndApply}
            disabled={
              isLoading || 
              !newPaymentData.payment_date || 
              !newPaymentData.amount || 
              amountToApply <= 0 ||
              (formaPagoRequiereCuentaBancaria(newPaymentData.forma_pago) && !newPaymentData.bank_account) ||
              ((newPaymentData.moneda || 'MXN') !== 'MXN' && !(parseFloat(newPaymentData.tipo_cambio) > 0))
            }
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creando y Aplicando...
              </>
            ) : (
              'Crear y Aplicar Pago'
            )}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>

    <RevisarAsientoContableModal
      show={showAsientoModal}
      onHide={() => {
        setShowAsientoModal(false)
        setPendingApply(null)
      }}
      title="Revisar asiento del cobro"
      propuesta={asientoPropuesta}
      isLoading={loadingAsientoPreview}
      error={asientoPreviewError}
      clientId={clienteId}
      isConfirming={applyPaymentMutation.isLoading}
      confirmLabel="Confirmar y aplicar pago"
      onConfirm={({ lineas_contables }) => {
        if (!pendingApply) return
        applyPaymentMutation.mutate({
          paymentId: pendingApply.paymentId,
          amount: pendingApply.amount,
          lineas_contables,
        })
      }}
    />
    </>
  )
}

export default ApplyPaymentModal
